import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherCheck,
  FeatherLoader,
  FeatherX,
} from "@subframe/core";
import {
  differenceInDays,
  differenceInHours,
  format,
  formatDistanceToNow,
  isPast,
} from "date-fns";

import { NumberInput } from "@/components/NumberInput";
import {
  ScheduledQuestionnaireListItemDto,
  ScheduledQuestionnaireListItemDtoMissBehaviour,
} from "@/generated/model";
import type { ScheduledQuestionnaireListItemWithSync } from "@/hooks/use-offline-scheduled-questionnaires";
import { formatUkDate, isUkToday } from "@/lib/date-utils";

export type DisplayStatus =
  | "due"
  | "overdue"
  | "in_progress"
  | "completed"
  | "completed_late"
  | "completed_syncing" // Completed locally but not yet synced
  | "missed"
  | "pending";

/**
 * Get the display status for a scheduled questionnaire item
 * Considers both server status and local completion state
 */
export function getDisplayStatus(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): DisplayStatus {
  const availableUntil = new Date(item.availableUntil);

  // Server says completed - trust it (highest priority)
  if (item.status === "COMPLETED_LATE") {
    return "completed_late";
  }
  if (item.status === "COMPLETED") {
    return "completed";
  }

  // Check for locally completed but unsynced
  const itemWithSync = item as ScheduledQuestionnaireListItemWithSync;
  if (itemWithSync.localSyncStatus?.isLocallyCompleted) {
    return "completed_syncing";
  }

  if (item.status === "EXPIRED") {
    return "missed";
  }

  if (item.status === "STARTED") {
    return "in_progress";
  }

  const availableFrom = new Date(item.availableFrom);

  if (isPast(availableUntil)) {
    return "overdue";
  }

  if (isPast(availableFrom)) {
    return "due";
  }

  return "pending";
}

/**
 * Check if a single answer value represents a failed item.
 * Centralised so the logic can be made more complex later
 * (e.g. handling numeric thresholds or multi-value answers).
 */
export function isFailedAnswer(
  answerValue: unknown,
  questionType?: string,
): boolean {
  if (
    questionType &&
    questionType !== "YES_NO" &&
    questionType !== "YES_NO_NA"
  ) {
    return false;
  }
  if (typeof answerValue === "string") {
    return answerValue.toLowerCase() === "no";
  }
  return false;
}

/**
 * Count the number of failed answers in a checklist
 */
export function countFailedAnswers(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): number {
  if (!item.answers) return 0;

  return item.answers.filter((a) =>
    isFailedAnswer(a.answerValue, a.questionType),
  ).length;
}

/**
 * Calculate days overdue (negative means not overdue yet)
 */
export function getDaysOverdue(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): number {
  const now = new Date();
  const availableUntil = new Date(item.availableUntil);
  return differenceInDays(now, availableUntil);
}

/**
 * Calculate days late for completed items
 */
export function getDaysLate(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): number {
  if (!item.completedAt) return 0;
  const completedAt = new Date(item.completedAt);
  const availableUntil = new Date(item.availableUntil);
  return differenceInDays(completedAt, availableUntil);
}

export interface TimeDisplayInfo {
  text: string;
  isError: boolean;
  useCalendarIcon: boolean;
}

/**
 * Get the time display info for the left side of a checklist row
 */
export function getTimeDisplayInfo(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): TimeDisplayInfo {
  const status = getDisplayStatus(item);
  const availableUntil = new Date(item.availableUntil);
  const scheduledDate = new Date(item.scheduledDate);

  // Completed late - show "x days late"
  if (status === "completed_late" && item.completedAt) {
    const daysLate = getDaysLate(item);
    if (daysLate >= 1) {
      return {
        text: `${daysLate} day${daysLate > 1 ? "s" : ""} late`,
        isError: true,
        useCalendarIcon: true,
      };
    }
    // Less than a day late, show hours
    const completedAt = new Date(item.completedAt);
    const hoursLate = differenceInHours(completedAt, availableUntil);
    return {
      text: `${hoursLate} hour${hoursLate > 1 ? "s" : ""} late`,
      isError: true,
      useCalendarIcon: true,
    };
  }

  // Completed on time - show "x hours/days ago"
  if (status === "completed" && item.completedAt) {
    return {
      text: `${formatDistanceToNow(new Date(item.completedAt))} ago`,
      isError: false,
      useCalendarIcon: false,
    };
  }

  // Completed locally but syncing - show "Just now" or similar
  if (status === "completed_syncing") {
    return {
      text: "Just now",
      isError: false,
      useCalendarIcon: false,
    };
  }

  // Missed/Expired - show "x days ago" in red
  if (status === "missed" || status === "overdue") {
    const daysOverdue = getDaysOverdue(item);
    if (daysOverdue >= 1) {
      return {
        text: `${daysOverdue} day${daysOverdue > 1 ? "s" : ""} ago`,
        isError: true,
        useCalendarIcon: true,
      };
    }
    return {
      text: `${formatDistanceToNow(availableUntil)} ago`,
      isError: true,
      useCalendarIcon: true,
    };
  }

  // Active window (due/in_progress). Key the relative-time copy off
  // `availableUntil` (the actual deadline), not `scheduledDate` (start of
  // window). For a Monthly with scheduledDate=Day 1 / availableUntil=Day 28
  // the two diverge — pre-fix this fell through to the "EEEE" fallback and
  // rendered "Friday" (the day name of scheduledDate) on every day of the
  // window (NAU-233 follow-on).
  if (status === "due" || status === "in_progress") {
    if (isUkToday(availableUntil)) {
      return {
        text: `Due in ${formatDistanceToNow(availableUntil)}`,
        isError: false,
        useCalendarIcon: false,
      };
    }
    return {
      text: `Due ${formatUkDate(availableUntil, "d MMM")}`,
      isError: false,
      useCalendarIcon: true,
    };
  }

  // Pending today - show when it becomes available (e.g. "Available at 2:00 PM")
  if (isUkToday(scheduledDate)) {
    const availableFrom = new Date(item.availableFrom);
    return {
      text: `Available at ${formatUkDate(availableFrom, "h:mm a")}`,
      isError: false,
      useCalendarIcon: false,
    };
  }

  // Pending/Upcoming future days - show day name (e.g. "Wednesday")
  return {
    text: format(scheduledDate, "EEEE"),
    isError: false,
    useCalendarIcon: true,
  };
}

export interface StatusBadgeInfo {
  issuesBadge: React.ReactNode | null;
  syncBadge: React.ReactNode | null;
}

/**
 * Get the issues/sync badges for the right side of a checklist row. The
 * row's status itself renders via the Workspace traffic-light pill — see
 * `lib/workspace/status-pill.ts`.
 */
export function getStatusBadges(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
  options?: { isOffline?: boolean },
): StatusBadgeInfo {
  const status = getDisplayStatus(item);
  const noAnswerCount = countFailedAnswers(item);
  const itemWithSync = item as ScheduledQuestionnaireListItemWithSync;
  const isOffline =
    options?.isOffline ??
    (typeof navigator !== "undefined" ? !navigator.onLine : false);

  // Issues badge (warning triangle with count) - for completed or expired items
  let issuesBadge: React.ReactNode | null = null;
  if (
    (status === "completed" ||
      status === "completed_late" ||
      status === "completed_syncing" ||
      status === "missed") &&
    noAnswerCount > 0
  ) {
    issuesBadge = (
      <Badge variant="error" icon={<FeatherAlertTriangle />}>
        {noAnswerCount}
      </Badge>
    );
  }

  // Sync badge - show pending or failed sync status
  let syncBadge: React.ReactNode | null = null;
  if (itemWithSync.localSyncStatus) {
    const { pendingCount, failedCount } = itemWithSync.localSyncStatus;

    if (failedCount > 0) {
      syncBadge = (
        <Badge variant="error" icon={<FeatherAlertCircle />}>
          Sync failed
        </Badge>
      );
    } else if (pendingCount > 0) {
      // Show different badge when offline vs online
      syncBadge = !isOffline ? (
        <Badge
          variant="neutral"
          icon={<FeatherLoader className="animate-spin" />}
        >
          Syncing
        </Badge>
      ) : (
        <Badge variant="warning">Pending sync</Badge>
      );
    }
  }

  return {
    issuesBadge,
    syncBadge,
  };
}

/**
 * Check if a checklist item is locked due to hard miss (expired + HARD_MISS behaviour)
 * These items can only be viewed, not started or continued.
 */
export function isHardMissLocked(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): boolean {
  const status = getDisplayStatus(item);
  return (
    (status === "missed" || status === "overdue") &&
    item.missBehaviour ===
      ScheduledQuestionnaireListItemDtoMissBehaviour.HARD_MISS
  );
}

/**
 * Check if a checklist item is in "future" state (can't be started yet)
 */
export function isFutureItem(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): boolean {
  const status = getDisplayStatus(item);
  return status === "pending";
}

/**
 * Check if a checklist item is completed (either on time, late, or syncing)
 */
export function isCompletedItem(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): boolean {
  const status = getDisplayStatus(item);
  return (
    status === "completed" ||
    status === "completed_late" ||
    status === "completed_syncing"
  );
}

/**
 * Get the button label for a checklist item
 */
export function getButtonLabel(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): string {
  const status = getDisplayStatus(item);
  if (isCompletedItem(item) || status === "missed") {
    return "View";
  }
  if (isHardMissLocked(item)) {
    return "View";
  }
  if (status === "in_progress") {
    return "Continue";
  }
  if (status === "pending") {
    return "Preview";
  }
  return "Start";
}

// ---------------------------------------------------------------------------
// Question-type helpers (shared across preview, edit, and execution views)
// ---------------------------------------------------------------------------

/** Canonical display labels keyed by the question-type string used in the API. */
export const QUESTION_TYPE_LABELS: Record<string, string> = {
  YES_NO: "Yes/No",
  NUMBER: "Number",
  TEXT: "Free text",
  RATING: "Rating",
};

/** Return a human-readable label for a question type string. */
export function getQuestionTypeLabel(questionType?: string): string {
  if (!questionType) return "Yes/No";
  return QUESTION_TYPE_LABELS[questionType] ?? questionType;
}

type AnswerValue = "yes" | "no" | "na" | number | string;

interface QuestionAnswerInputProps {
  questionType?: string;
  /** Current answer value (omit for preview-only mode). */
  answerValue?: AnswerValue;
  /** Called when the user selects/enters an answer. Omit for read-only. */
  onAnswer?: (value: AnswerValue) => void;
  /** Disables all inputs. Also true when onAnswer is omitted. */
  readOnly?: boolean;
}

/**
 * Renders the answer input for a question based on its type.
 *
 * - **Preview mode** (no `onAnswer`): static disabled inputs.
 * - **Interactive mode** (`onAnswer` provided): fully functional inputs
 *   with the same visuals used by both the execution and preview views.
 */
export function QuestionAnswerInput({
  questionType,
  answerValue,
  onAnswer,
  readOnly: readOnlyProp,
}: QuestionAnswerInputProps) {
  const isReadOnly = readOnlyProp ?? !onAnswer;

  if (questionType === "NUMBER") {
    return (
      <TextField className="h-auto w-full flex-none" size="large">
        <NumberInput
          placeholder="Enter value"
          value={typeof answerValue === "number" ? answerValue : null}
          onValueChange={(v) => {
            if (!onAnswer) return;
            if (v === null) return;
            onAnswer(v);
          }}
          disabled={isReadOnly}
        />
      </TextField>
    );
  }

  if (questionType === "RATING") {
    return (
      <div className="flex w-full items-center gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <Button
            key={n}
            className={`h-12 grow shrink-0 basis-0 ${isReadOnly ? "pointer-events-none" : ""}`}
            variant={
              answerValue === n ? "positive-secondary" : "neutral-primary"
            }
            size="large"
            onClick={() => onAnswer?.(n)}
            disabled={isReadOnly && answerValue !== n}
          >
            {n}
          </Button>
        ))}
      </div>
    );
  }

  if (questionType === "TEXT" || questionType === "LONG_TEXT") {
    return (
      <TextArea
        className="h-auto w-full flex-none"
        variant="outline"
        label=""
        helpText=""
      >
        <TextArea.Input
          placeholder="Enter text..."
          value={typeof answerValue === "string" ? answerValue : ""}
          onChange={(e) => onAnswer?.(e.target.value)}
          disabled={isReadOnly}
        />
      </TextArea>
    );
  }

  // Default: YES_NO
  return (
    <div className="flex w-full items-center gap-2">
      <Button
        className={`h-12 grow shrink-0 basis-0 ${isReadOnly ? "pointer-events-none" : ""}`}
        variant={
          answerValue === "yes" ? "positive-secondary" : "neutral-primary"
        }
        size="large"
        icon={<FeatherCheck />}
        onClick={() => onAnswer?.("yes")}
        disabled={isReadOnly && answerValue !== "yes"}
      >
        Yes
      </Button>
      <Button
        className={`h-12 grow shrink-0 basis-0 ${isReadOnly ? "pointer-events-none" : ""}`}
        variant={
          isFailedAnswer(answerValue, questionType)
            ? "destructive-primary"
            : "neutral-primary"
        }
        size="large"
        icon={<FeatherX />}
        onClick={() => onAnswer?.("no")}
        disabled={isReadOnly && !isFailedAnswer(answerValue, questionType)}
      >
        No
      </Button>
    </div>
  );
}
